/**
 * PTEL Tipología → Símbolo Matcher
 * Detección automática de símbolos basada en tipología de elemento vulnerable
 *
 * @module tipologiaSymbolMatcher
 * @version 2.0.0
 * @date 2024-12-28
 * @description Sistema de asignación automática de simbología PTEL basado en
 *              análisis de texto descriptivo con keywords normalizados.
 *
 * @example
 * ```typescript
 * import { detectAndGetSymbol } from '@/lib/symbols/tipologiaSymbolMatcher';
 *
 * const result = detectAndGetSymbol("Centro de Salud La Villa", "12mm", "PNG");
 * // → { tipologia: "CENTRO_SANITARIO", simbolo: "CENTROS_SANITARIOS", path: "..." }
 * ```
 */

import { TipologiaPTEL } from '@/types/tipologia';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Categorías agrupadoras del visor PTEL.
 *
 * 36 categorías macro que el visor usa para clasificar elementos vulnerables y
 * elegir qué icono pintar. NO confundir con el macro `TipologiaPTEL` (19 cat,
 * canónico legal) ni con `SimboloIcono` (36 IDs físicos del catálogo PNG).
 *
 * Refactor TUN4-A bloque 2/2 (7-may-2026): convertido de `type TipologiaPTEL`
 * (union string literal 36) a `enum CategoriaSimbolo` (36) para coherencia con
 * TUN2 (`enum TipologiaPTEL` macro 19) y TUN3 (`enum CodigoFuente`). El nombre
 * `CategoriaSimbolo` resuelve el reuso conflictivo de `TipologiaPTEL` detectado
 * por la auditoría TUN4-AUDIT (Hallazgo 1).
 *
 * Ver `.ptel/ADENDA_DECISION_TIPOLOGIA_UNIFY_20260507_AUDIT.md` §2-3.
 */
export enum CategoriaSimbolo {
  // Servicios
  CENTRO_SANITARIO = 'CENTRO_SANITARIO',
  CENTRO_ENSENANZA = 'CENTRO_ENSENANZA',
  ALOJAMIENTO = 'ALOJAMIENTO',
  CONCURRENCIA_PUBLICA = 'CONCURRENCIA_PUBLICA',
  SERVICIOS_SOCIALES = 'SERVICIOS_SOCIALES',
  BIBLIOTECA = 'BIBLIOTECA',
  // Emergencias
  CECOPAL = 'CECOPAL',
  BOMBEROS = 'BOMBEROS',
  INFOCA = 'INFOCA',
  POLICIA_LOCAL = 'POLICIA_LOCAL',
  POLICIA_NACIONAL = 'POLICIA_NACIONAL',
  GUARDIA_CIVIL = 'GUARDIA_CIVIL',
  PROTECCION_CIVIL = 'PROTECCION_CIVIL',
  APOYO_LOGISTICO = 'APOYO_LOGISTICO',
  // Transportes
  HELIPUERTO = 'HELIPUERTO',
  GASOLINERA = 'GASOLINERA',
  ANTENAS_COMUNICACIONES = 'ANTENAS_COMUNICACIONES',
  // Hidrografía
  DEPOSITO_AGUA = 'DEPOSITO_AGUA',
  ETAP = 'ETAP',
  EDAR = 'EDAR',
  // Energía
  CENTRAL_ELECTRICA = 'CENTRAL_ELECTRICA',
  SUBESTACION_ELECTRICA = 'SUBESTACION_ELECTRICA',
  ENERGIA_EOLICA = 'ENERGIA_EOLICA',
  ENERGIA_SOLAR = 'ENERGIA_SOLAR',
  // Industria
  INDUSTRIA = 'INDUSTRIA',
  SUSTANCIAS_PELIGROSAS = 'SUSTANCIAS_PELIGROSAS',
  // Patrimonio
  ELEMENTO_HISTORICO = 'ELEMENTO_HISTORICO',
  EDIFICIO_RELIGIOSO = 'EDIFICIO_RELIGIOSO',
  CAVIDAD = 'CAVIDAD',
  ESPACIO_NATURAL = 'ESPACIO_NATURAL',
  // Equipamientos
  EDIFICIO_PUBLICO = 'EDIFICIO_PUBLICO',
  CENTRO_CULTURAL = 'CENTRO_CULTURAL',
  ESPACIO_CULTURAL = 'ESPACIO_CULTURAL',
  AREA_RECREATIVA = 'AREA_RECREATIVA',
  PARQUE_OCIO = 'PARQUE_OCIO',
  DEPOSITO_INDUSTRIAL = 'DEPOSITO_INDUSTRIAL',
}

/**
 * Lista congelada de los 36 valores del enum CategoriaSimbolo, en orden de declaración.
 * Útil para iteración exhaustiva, paridad disco↔tipo, y tests.
 */
export const CATEGORIAS_SIMBOLO_VALUES: readonly CategoriaSimbolo[] = Object.freeze(
  Object.values(CategoriaSimbolo),
);


/**
 * Identificadores de símbolos disponibles en el catálogo.
 * 36 símbolos normalizados extraídos de SIMBOLOGIA_PTEL.odt.
 *
 * Refactor TUN4-A (7-may-2026): convertido de `type SimboloPTEL` (union string literal de 38)
 * a `enum SimboloIcono` (36) tras eliminación de huérfanos sin archivo físico
 * (`ALMACEN_SUST_PELIG_2`, `PROTECCION_CIVIL_INT` — Hallazgo 3 ADENDA TUN4-AUDIT).
 * Ver `.ptel/ADENDA_DECISION_TIPOLOGIA_UNIFY_20260507_AUDIT.md` §2-3.
 */
export enum SimboloIcono {
  ALMACEN_SUST_PELIG = 'ALMACEN_SUST_PELIG',
  ALOJAMIENTOS = 'ALOJAMIENTOS',
  ANTENAS = 'ANTENAS',
  APOYO_LOGISTICO = 'APOYO_LOGISTICO',
  BIBLIOTECA = 'BIBLIOTECA',
  BOMBEROS = 'BOMBEROS',
  CAVIDADES = 'CAVIDADES',
  CECOPAL = 'CECOPAL',
  CENTRAL_ELECTRICA = 'CENTRAL_ELECTRICA',
  CENTROS_EDUCATIVOS = 'CENTROS_EDUCATIVOS',
  CENTROS_SANITARIOS = 'CENTROS_SANITARIOS',
  CENTRO_CULTURAL = 'CENTRO_CULTURAL',
  DEPOSITOS = 'DEPOSITOS',
  DEPOSITOS_2 = 'DEPOSITOS_2',
  EDAR = 'EDAR',
  EDIFICIOS_PUBLICOS = 'EDIFICIOS_PUBLICOS',
  EDIFICIO_RELIGIOSO = 'EDIFICIO_RELIGIOSO',
  ELEMENTO_HISTORICO = 'ELEMENTO_HISTORICO',
  ENERGIA_EOLICA = 'ENERGIA_EOLICA',
  ENERGIA_SOLAR = 'ENERGIA_SOLAR',
  EQUIPAMIENTO_NATURALEZA = 'EQUIPAMIENTO_NATURALEZA',
  ESPACIOS_CULTURALES = 'ESPACIOS_CULTURALES',
  ETAP = 'ETAP',
  GASOLINERA = 'GASOLINERA',
  GRANDES_CONCENTRACIONES = 'GRANDES_CONCENTRACIONES',
  GUARDIA_CIVIL = 'GUARDIA_CIVIL',
  HELIPUERTO = 'HELIPUERTO',
  INDUSTRIA = 'INDUSTRIA',
  INFOCA = 'INFOCA',
  PARQUE_NATURAL = 'PARQUE_NATURAL',
  PARQUE_OCIO = 'PARQUE_OCIO',
  POLICIA_LOCAL = 'POLICIA_LOCAL',
  POLICIA_NACIONAL = 'POLICIA_NACIONAL',
  PROTECCION_CIVIL_ESP = 'PROTECCION_CIVIL_ESP',
  SERVICIOS_SOCIALES = 'SERVICIOS_SOCIALES',
  SUBESTACION_ELECTRICA = 'SUBESTACION_ELECTRICA',
}

/**
 * Lista congelada de los 36 valores del enum SimboloIcono, en orden de declaración.
 * Útil para iteración exhaustiva, paridad disco↔tipo, y tests.
 */
export const SIMBOLOS_ICONO_VALUES: readonly SimboloIcono[] = Object.freeze(
  Object.values(SimboloIcono),
);

/**
 * Tamaños disponibles en milímetros (resolución 96 DPI)
 */
export type SizeCode = '06mm' | '08mm' | '12mm' | '20mm' | '28mm';

/**
 * Formatos de imagen disponibles
 */
export type ImageFormat = 'PNG';

/**
 * Categorías de agrupación de tipologías
 */
export type CategoriaPTEL =
  | 'SERVICIOS'
  | 'EMERGENCIAS'
  | 'TRANSPORTES'
  | 'HIDROGRAFIA'
  | 'ENERGIA'
  | 'INDUSTRIA'
  | 'PATRIMONIO'
  | 'EQUIPAMIENTOS';

/**
 * Configuración de una tipología con keywords y metadatos
 */
export interface CategoriaSimboloConfig {
  /** Símbolo principal asignado */
  simbolo: SimboloIcono;
  /** Símbolo alternativo (opcional) */
  simboloAlt?: SimboloIcono;
  /** Descripción legible */
  descripcion: string;
  /** Lista de keywords para detección */
  keywords: string[];
  /** Categoría de agrupación */
  categoria: CategoriaPTEL;
  /** Referencia a capa DERA (opcional) */
  dera?: string;
}

/**
 * Resultado de la detección automática
 */
export interface DetectionResult {
  /** Tipología detectada */
  tipologia: CategoriaSimbolo;
  /** Símbolo asignado */
  simbolo: SimboloIcono;
  /** Ruta al archivo de imagen */
  path: string;
  /** Score de confianza (longitud del keyword coincidente) */
  confidence: number;
  /** Keyword que produjo el match */
  matchedKeyword: string;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Conversión de tamaños mm a píxeles (96 DPI)
 */
export const SIZE_TO_PIXELS: Record<SizeCode, number> = {
  '06mm': 23,
  '08mm': 30,
  '12mm': 45,
  '20mm': 76,
  '28mm': 106,
};

/**
 * Ruta base para los símbolos en el proyecto
 * Usa import.meta.env.BASE_URL para compatibilidad con Vite base path
 */
export const SYMBOLS_BASE_PATH = `${import.meta.env.BASE_URL}assets/symbols`;

// ============================================================================
// DICCIONARIO DE TIPOLOGÍAS
// ============================================================================

/**
 * Mapeo completo de tipologías PTEL con sus configuraciones
 * Incluye keywords para detección automática y metadatos DERA
 */
export const CATEGORIA_MAP: Record<CategoriaSimbolo, CategoriaSimboloConfig> = {
  // ══════════════════════════════════════════════════════════════════════════
  // SERVICIOS
  // ══════════════════════════════════════════════════════════════════════════
  CENTRO_SANITARIO: {
    simbolo: SimboloIcono.CENTROS_SANITARIOS,
    descripcion: 'Hospitales, centros de salud, consultorios',
    categoria: 'SERVICIOS',
    keywords: [
      'hospital',
      'centro salud',
      'consultorio',
      'sanitario',
      'médico',
      'medico',
      'urgencias',
      'sas',
      'ambulatorio',
      'clínica',
      'clinica',
    ],
    dera: 'G12 - g12_01_CentroSalud, g12_02_Hospital_CAE',
  },
  CENTRO_ENSENANZA: {
    simbolo: SimboloIcono.CENTROS_EDUCATIVOS,
    descripcion: 'Colegios, institutos, escuelas, universidades',
    categoria: 'SERVICIOS',
    keywords: [
      'colegio',
      'instituto',
      'escuela',
      'ceip',
      'ies',
      'universidad',
      'educativo',
      'docente',
      'guardería',
      'guarderia',
      'enseñanza',
      'ensenanza',
    ],
    dera: 'Consejería Educación',
  },
  ALOJAMIENTO: {
    simbolo: SimboloIcono.ALOJAMIENTOS,
    descripcion: 'Hoteles, casas rurales, albergues, campings',
    categoria: 'SERVICIOS',
    keywords: [
      'hotel',
      'hostal',
      'casa rural',
      'albergue',
      'camping',
      'alojamiento',
      'pensión',
      'pension',
      'hospedaje',
      'apartamento turístico',
    ],
    dera: 'Registro Turismo Andalucía',
  },
  CONCURRENCIA_PUBLICA: {
    simbolo: SimboloIcono.GRANDES_CONCENTRACIONES,
    descripcion: 'Centros comerciales, áreas de destacada concurrencia',
    categoria: 'SERVICIOS',
    keywords: [
      'centro comercial',
      'concurrencia',
      'concentración',
      'concentracion',
      'aforo',
      'multitud',
      'grandes superficies',
      'hipermercado',
    ],
  },
  SERVICIOS_SOCIALES: {
    simbolo: SimboloIcono.SERVICIOS_SOCIALES,
    descripcion: 'Centros de servicios sociales, residencias',
    categoria: 'SERVICIOS',
    keywords: [
      'servicios sociales',
      'residencia mayores',
      'residencia ancianos',
      'dependencia',
      'asistencia social',
      'centro día',
      'centro dia',
      'tercera edad',
    ],
  },
  BIBLIOTECA: {
    simbolo: SimboloIcono.BIBLIOTECA,
    descripcion: 'Bibliotecas públicas',
    categoria: 'SERVICIOS',
    keywords: ['biblioteca', 'mediateca', 'archivo municipal'],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // EMERGENCIAS
  // ══════════════════════════════════════════════════════════════════════════
  CECOPAL: {
    simbolo: SimboloIcono.CECOPAL,
    descripcion: 'Centro de Coordinación Operativa Local',
    categoria: 'EMERGENCIAS',
    keywords: ['cecopal', 'coordinación operativa', 'cecop', 'centro emergencias', 'puesto mando'],
  },
  BOMBEROS: {
    simbolo: SimboloIcono.BOMBEROS,
    descripcion: 'Parques de bomberos',
    categoria: 'EMERGENCIAS',
    keywords: ['bomberos', 'parque bomberos', 'contraincendios', 'extinción', 'extincion'],
  },
  INFOCA: {
    simbolo: SimboloIcono.INFOCA,
    descripcion: 'Centros INFOCA, incendios forestales',
    categoria: 'EMERGENCIAS',
    keywords: ['infoca', 'cedefo', 'forestal', 'incendio forestal', 'brif'],
  },
  POLICIA_LOCAL: {
    simbolo: SimboloIcono.POLICIA_LOCAL,
    descripcion: 'Comisarías de Policía Local',
    categoria: 'EMERGENCIAS',
    keywords: ['policía local', 'policia local', 'municipal', 'jefatura policía local'],
  },
  POLICIA_NACIONAL: {
    simbolo: SimboloIcono.POLICIA_NACIONAL,
    descripcion: 'Comisarías de Policía Nacional',
    categoria: 'EMERGENCIAS',
    keywords: ['policía nacional', 'policia nacional', 'cnp', 'comisaría policía'],
  },
  GUARDIA_CIVIL: {
    simbolo: SimboloIcono.GUARDIA_CIVIL,
    descripcion: 'Cuarteles de la Guardia Civil',
    categoria: 'EMERGENCIAS',
    keywords: ['guardia civil', 'cuartel', 'benemérita', 'benemerita', 'seprona'],
  },
  PROTECCION_CIVIL: {
    simbolo: SimboloIcono.PROTECCION_CIVIL_ESP,
    descripcion: 'Agrupaciones de Protección Civil',
    categoria: 'EMERGENCIAS',
    keywords: ['protección civil', 'proteccion civil', 'voluntarios emergencia', 'apc', 'agrupación voluntarios'],
  },
  APOYO_LOGISTICO: {
    simbolo: SimboloIcono.APOYO_LOGISTICO,
    descripcion: 'Puntos de apoyo logístico',
    categoria: 'EMERGENCIAS',
    keywords: ['apoyo logístico', 'apoyo logistico', 'logística emergencia', 'almacén emergencias', 'punto encuentro'],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // TRANSPORTES
  // ══════════════════════════════════════════════════════════════════════════
  HELIPUERTO: {
    simbolo: SimboloIcono.HELIPUERTO,
    descripcion: 'Helipuertos, bases aéreas',
    categoria: 'TRANSPORTES',
    keywords: ['helipuerto', 'helicóptero', 'helicoptero', 'base aérea', 'aeródromo', 'aerodromo', 'helibase'],
    dera: 'G08 Transportes',
  },
  GASOLINERA: {
    simbolo: SimboloIcono.GASOLINERA,
    descripcion: 'Estaciones de servicio',
    categoria: 'TRANSPORTES',
    keywords: ['gasolinera', 'estación servicio', 'estacion servicio', 'gasolina', 'combustible', 'área servicio'],
    dera: 'G08 Transportes - Estaciones servicio',
  },
  ANTENAS_COMUNICACIONES: {
    simbolo: SimboloIcono.ANTENAS,
    descripcion: 'Torres de telecomunicaciones',
    categoria: 'TRANSPORTES',
    keywords: ['antena', 'repetidor', 'torre comunicaciones', 'telecomunicaciones', 'radio', 'tetra'],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // HIDROGRAFÍA
  // ══════════════════════════════════════════════════════════════════════════
  DEPOSITO_AGUA: {
    simbolo: SimboloIcono.DEPOSITOS,
    descripcion: 'Depósitos de agua potable',
    categoria: 'HIDROGRAFIA',
    keywords: ['depósito agua', 'deposito agua', 'aljibe', 'cisterna', 'abastecimiento agua', 'depósito municipal'],
    dera: 'G06 Hidrografía',
  },
  ETAP: {
    simbolo: SimboloIcono.ETAP,
    descripcion: 'Estación Tratamiento Agua Potable',
    categoria: 'HIDROGRAFIA',
    keywords: ['etap', 'potabilizadora', 'tratamiento agua', 'planta potabilizadora'],
    dera: 'G06 Hidrografía - ETAP',
  },
  EDAR: {
    simbolo: SimboloIcono.EDAR,
    descripcion: 'Estación Depuradora Aguas Residuales',
    categoria: 'HIDROGRAFIA',
    keywords: ['edar', 'depuradora', 'residuales', 'saneamiento', 'aguas residuales'],
    dera: 'G06 Hidrografía - EDAR',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // ENERGÍA
  // ══════════════════════════════════════════════════════════════════════════
  CENTRAL_ELECTRICA: {
    simbolo: SimboloIcono.CENTRAL_ELECTRICA,
    descripcion: 'Centrales eléctricas',
    categoria: 'ENERGIA',
    keywords: ['central eléctrica', 'central electrica', 'generadora', 'planta eléctrica', 'central térmica'],
    dera: 'G07 Energía',
  },
  SUBESTACION_ELECTRICA: {
    simbolo: SimboloIcono.SUBESTACION_ELECTRICA,
    descripcion: 'Subestaciones eléctricas',
    categoria: 'ENERGIA',
    keywords: ['subestación', 'subestacion', 'transformador', 'distribución eléctrica', 'línea alta tensión'],
    dera: 'G07 Energía - Subestaciones',
  },
  ENERGIA_EOLICA: {
    simbolo: SimboloIcono.ENERGIA_EOLICA,
    descripcion: 'Parques eólicos',
    categoria: 'ENERGIA',
    keywords: ['eólica', 'eolica', 'aerogenerador', 'parque eólico', 'molino viento'],
    dera: 'G07 Energía - REDIAM',
  },
  ENERGIA_SOLAR: {
    simbolo: SimboloIcono.ENERGIA_SOLAR,
    descripcion: 'Plantas solares',
    categoria: 'ENERGIA',
    keywords: ['solar', 'fotovoltaica', 'placa solar', 'huerto solar', 'termosolar', 'paneles solares'],
    dera: 'G07 Energía',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // INDUSTRIA
  // ══════════════════════════════════════════════════════════════════════════
  INDUSTRIA: {
    simbolo: SimboloIcono.INDUSTRIA,
    descripcion: 'Polígonos industriales',
    categoria: 'INDUSTRIA',
    keywords: ['industria', 'polígono industrial', 'poligono industrial', 'fábrica', 'fabrica', 'nave industrial', 'agroalimentaria'],
    dera: 'G05 Actividades',
  },
  SUSTANCIAS_PELIGROSAS: {
    simbolo: SimboloIcono.ALMACEN_SUST_PELIG,
    descripcion: 'Almacenamiento sustancias peligrosas',
    categoria: 'INDUSTRIA',
    keywords: ['sustancias peligrosas', 'seveso', 'químico', 'quimico', 'peligroso', 'explosivo', 'inflamable', 'tóxico'],
    dera: 'Registro SEVESO, ICA',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // PATRIMONIO
  // ══════════════════════════════════════════════════════════════════════════
  ELEMENTO_HISTORICO: {
    simbolo: SimboloIcono.ELEMENTO_HISTORICO,
    descripcion: 'Patrimonio histórico',
    categoria: 'PATRIMONIO',
    keywords: ['histórico', 'historico', 'yacimiento', 'monumento', 'bic', 'patrimonio', 'castillo', 'ruinas', 'arqueológico'],
    dera: 'G09 Patrimonio - IAPH',
  },
  EDIFICIO_RELIGIOSO: {
    simbolo: SimboloIcono.EDIFICIO_RELIGIOSO,
    descripcion: 'Iglesias, ermitas',
    categoria: 'PATRIMONIO',
    keywords: ['iglesia', 'ermita', 'parroquia', 'convento', 'monasterio', 'capilla', 'religioso', 'catedral', 'basílica'],
    dera: 'G09 Patrimonio',
  },
  CAVIDAD: {
    simbolo: SimboloIcono.CAVIDADES,
    descripcion: 'Cuevas, simas',
    categoria: 'PATRIMONIO',
    keywords: ['cueva', 'cavidad', 'sima', 'gruta', 'espeleología', 'espeleologia'],
    dera: 'G10 Medio Natural',
  },
  ESPACIO_NATURAL: {
    simbolo: SimboloIcono.PARQUE_NATURAL,
    descripcion: 'Espacios naturales protegidos',
    categoria: 'PATRIMONIO',
    keywords: ['parque natural', 'reserva natural', 'enp', 'espacio protegido', 'renpa', 'paraje natural'],
    dera: 'G10 Medio Natural - RENPA',
  },

  // ══════════════════════════════════════════════════════════════════════════
  // EQUIPAMIENTOS
  // ══════════════════════════════════════════════════════════════════════════
  EDIFICIO_PUBLICO: {
    simbolo: SimboloIcono.EDIFICIOS_PUBLICOS,
    descripcion: 'Ayuntamientos, edificios oficiales',
    categoria: 'EQUIPAMIENTOS',
    keywords: ['ayuntamiento', 'edificio público', 'edificio publico', 'oficial', 'administración', 'casa consistorial', 'juzgado'],
    dera: 'ISE',
  },
  CENTRO_CULTURAL: {
    simbolo: SimboloIcono.CENTRO_CULTURAL,
    descripcion: 'Centros culturales, museos',
    categoria: 'EQUIPAMIENTOS',
    keywords: ['centro cultural', 'museo', 'casa cultura', 'cultural', 'centro cívico', 'centro civico'],
  },
  ESPACIO_CULTURAL: {
    simbolo: SimboloIcono.ESPACIOS_CULTURALES,
    descripcion: 'Teatros, auditorios',
    categoria: 'EQUIPAMIENTOS',
    keywords: ['teatro', 'auditorio', 'escenario', 'espacio escénico', 'sala exposiciones', 'cine'],
  },
  AREA_RECREATIVA: {
    simbolo: SimboloIcono.EQUIPAMIENTO_NATURALEZA,
    descripcion: 'Áreas recreativas naturales',
    categoria: 'EQUIPAMIENTOS',
    keywords: ['área recreativa', 'area recreativa', 'merendero', 'zona picnic', 'equipamiento naturaleza', 'sendero'],
    dera: 'G10 Medio Natural',
  },
  PARQUE_OCIO: {
    simbolo: SimboloIcono.PARQUE_OCIO,
    descripcion: 'Parques de ocio urbanos',
    categoria: 'EQUIPAMIENTOS',
    keywords: ['parque ocio', 'zona verde', 'parque infantil', 'jardín público', 'jardin publico', 'recreativo', 'polideportivo'],
  },
  DEPOSITO_INDUSTRIAL: {
    simbolo: SimboloIcono.DEPOSITOS_2,
    descripcion: 'Depósitos industriales',
    categoria: 'EQUIPAMIENTOS',
    keywords: ['depósito industrial', 'deposito industrial', 'tanque', 'silo', 'almacenamiento industrial'],
  },
};

// ============================================================================
// FUNCIONES DE UTILIDAD
// ============================================================================

/**
 * Normaliza texto para comparación eliminando acentos y convirtiendo a minúsculas
 * @param texto - Texto a normalizar
 * @returns Texto normalizado sin acentos en minúsculas
 */
function normalizeText(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Detecta la tipología PTEL basándose en el texto descriptivo
 *
 * Utiliza un sistema de scoring donde el keyword más largo que coincide
 * determina la tipología. Esto favorece coincidencias más específicas.
 *
 * @param texto - Texto descriptivo del elemento (nombre + tipo)
 * @returns Tipología detectada o null si no hay coincidencia
 *
 * @example
 * ```typescript
 * detectTipologia("Centro de Salud La Villa");
 * // → "CENTRO_SANITARIO"
 *
 * detectTipologia("CEIP San Sebastián - Centro Educativo");
 * // → "CENTRO_ENSENANZA"
 * ```
 */
export function detectTipologia(texto: string): CategoriaSimbolo | null {
  const textoNorm = normalizeText(texto);

  let mejorMatch: CategoriaSimbolo | null = null;
  let mejorScore = 0;

  for (const [tipologia, config] of Object.entries(CATEGORIA_MAP) as [CategoriaSimbolo, CategoriaSimboloConfig][]) {
    for (const keyword of config.keywords) {
      const keywordNorm = normalizeText(keyword);
      if (textoNorm.includes(keywordNorm)) {
        const score = keywordNorm.length;
        if (score > mejorScore) {
          mejorScore = score;
          mejorMatch = tipologia;
        }
      }
    }
  }

  return mejorMatch;
}

/**
 * Detecta tipología con información detallada del match
 *
 * @param texto - Texto descriptivo del elemento
 * @returns Objeto con tipología, score y keyword coincidente, o null
 */
export function detectTipologiaDetailed(
  texto: string
): { tipologia: CategoriaSimbolo; confidence: number; matchedKeyword: string } | null {
  const textoNorm = normalizeText(texto);

  let mejorMatch: CategoriaSimbolo | null = null;
  let mejorScore = 0;
  let mejorKeyword = '';

  for (const [tipologia, config] of Object.entries(CATEGORIA_MAP) as [CategoriaSimbolo, CategoriaSimboloConfig][]) {
    for (const keyword of config.keywords) {
      const keywordNorm = normalizeText(keyword);
      if (textoNorm.includes(keywordNorm)) {
        const score = keywordNorm.length;
        if (score > mejorScore) {
          mejorScore = score;
          mejorMatch = tipologia;
          mejorKeyword = keyword;
        }
      }
    }
  }

  if (!mejorMatch) return null;

  return {
    tipologia: mejorMatch,
    confidence: mejorScore,
    matchedKeyword: mejorKeyword,
  };
}

/**
 * Obtiene el símbolo correspondiente a una tipología
 *
 * @param tipologia - Tipología PTEL
 * @param useAlt - Si es true y existe símbolo alternativo, lo devuelve
 * @returns Identificador del símbolo
 */
export function getSimboloForCategoria(tipologia: CategoriaSimbolo, useAlt = false): SimboloIcono {
  const config = CATEGORIA_MAP[tipologia];
  if (useAlt && config.simboloAlt) {
    return config.simboloAlt;
  }
  return config.simbolo;
}

/**
 * Obtiene la ruta del archivo de símbolo
 *
 * @param simbolo - Identificador del símbolo
 * @param size - Tamaño en mm (default: 12mm)
 * @param format - Formato de imagen (default: PNG)
 * @returns Ruta relativa al archivo
 *
 * @example
 * ```typescript
 * getSymbolPath("CENTROS_SANITARIOS", "12mm", "PNG");
 * // → "/assets/symbols/PNG/12mm/CENTROS_SANITARIOS.png"
 * ```
 */
export function getSymbolPath(simbolo: SimboloIcono, size: SizeCode = '12mm', format: ImageFormat = 'PNG'): string {
  const ext = format.toLowerCase();
  return `${SYMBOLS_BASE_PATH}/${format}/${size}/${simbolo}.${ext}`;
}

/**
 * Detecta y obtiene el símbolo directamente desde texto
 *
 * Función de conveniencia que combina detección y obtención de ruta
 *
 * @param texto - Texto descriptivo del elemento
 * @param size - Tamaño en mm
 * @param format - Formato de imagen
 * @returns Resultado completo de detección o null
 *
 * @example
 * ```typescript
 * const result = detectAndGetSymbol("Parque de Bomberos Municipal");
 * // → {
 * //     tipologia: "BOMBEROS",
 * //     simbolo: "BOMBEROS",
 * //     path: "/assets/symbols/PNG/12mm/BOMBEROS.png",
 * //     confidence: 15,
 * //     matchedKeyword: "parque bomberos"
 * //   }
 * ```
 */
export function detectAndGetSymbol(
  texto: string,
  size: SizeCode = '12mm',
  format: ImageFormat = 'PNG'
): DetectionResult | null {
  const detection = detectTipologiaDetailed(texto);
  if (!detection) return null;

  const simbolo = getSimboloForCategoria(detection.tipologia);
  const path = getSymbolPath(simbolo, size, format);

  return {
    tipologia: detection.tipologia,
    simbolo,
    path,
    confidence: detection.confidence,
    matchedKeyword: detection.matchedKeyword,
  };
}

/**
 * Obtiene todas las tipologías disponibles
 * @returns Array con todas las tipologías
 */
export function getAllCategorias(): CategoriaSimbolo[] {
  return Object.keys(CATEGORIA_MAP) as CategoriaSimbolo[];
}

/**
 * Obtiene tipologías filtradas por categoría
 * @param categoria - Categoría a filtrar
 * @returns Array de tipologías de esa categoría
 */
export function getCategoriasByGrupo(categoria: CategoriaPTEL): CategoriaSimbolo[] {
  return (Object.entries(CATEGORIA_MAP) as [CategoriaSimbolo, CategoriaSimboloConfig][])
    .filter(([, config]) => config.categoria === categoria)
    .map(([tipologia]) => tipologia);
}

/**
 * Obtiene la configuración completa de una tipología
 * @param tipologia - Tipología a consultar
 * @returns Configuración completa
 */
export function getCategoriaConfig(tipologia: CategoriaSimbolo): CategoriaSimboloConfig {
  return CATEGORIA_MAP[tipologia];
}

/**
 * Lista todos los símbolos disponibles
 * @returns Array con todos los identificadores de símbolos
 */
export function getAllSimbolos(): SimboloIcono[] {
  const simbolos = new Set<SimboloIcono>();
  for (const config of Object.values(CATEGORIA_MAP)) {
    simbolos.add(config.simbolo);
    if (config.simboloAlt) {
      simbolos.add(config.simboloAlt);
    }
  }
  return Array.from(simbolos).sort();
}

/**
 * Obtiene las categorías disponibles con sus tipologías
 * @returns Mapa de categoría a lista de tipologías
 */
export function getCategorias(): Record<CategoriaPTEL, CategoriaSimbolo[]> {
  const categorias: Record<CategoriaPTEL, CategoriaSimbolo[]> = {
    SERVICIOS: [],
    EMERGENCIAS: [],
    TRANSPORTES: [],
    HIDROGRAFIA: [],
    ENERGIA: [],
    INDUSTRIA: [],
    PATRIMONIO: [],
    EQUIPAMIENTOS: [],
  };

  for (const [tipologia, config] of Object.entries(CATEGORIA_MAP) as [CategoriaSimbolo, CategoriaSimboloConfig][]) {
    categorias[config.categoria].push(tipologia);
  }

  return categorias;
}

// ============================================================================
// ADAPTADOR FORMAL · CategoriaSimbolo (36) → TipologiaPTEL macro (19)
// ============================================================================

/**
 * Adaptador formal `categoriaAMacro: (c: CategoriaSimbolo) => TipologiaPTEL`.
 *
 * Reduce una categoría agrupadora del visor (granularidad 36) a su tipología
 * macro canónica del Decreto 69/2024 Anexo 1 (granularidad 19). Permite que
 * pipelines macro (export, geocoding, validaciones) consuman registros que
 * llegan con `CategoriaSimbolo` granular sin perder la semántica.
 *
 * Implementado como switch exhaustivo + `assertNever` para que TS verifique
 * en compile-time que las 36 entradas están cubiertas: si en el futuro se
 * añade una nueva entrada al enum `CategoriaSimbolo` y se olvida actualizar
 * este switch, TS rompe la compilación.
 *
 * Mapeo definitivo: ver `.ptel/ADENDA_DECISION_TIPOLOGIA_UNIFY_20260507_AUDIT.md` §3.
 *
 * Cobertura por macro:
 * - 18 de 19 macros reciben al menos una `CategoriaSimbolo`.
 * - `PUERTO` y `RSU` no reciben ninguna (deuda de catálogo, no de tipos).
 * - `GENERICO` absorbe los 4 casos sin macro estructural propia
 *   (alojamiento, concurrencia, cavidad, espacio natural).
 *
 * @param c - Categoría granular del visor.
 * @returns Tipología macro PTEL correspondiente.
 *
 * @example
 * ```typescript
 * categoriaAMacro(CategoriaSimbolo.CENTRO_SANITARIO);   // → TipologiaPTEL.SANITARIO
 * categoriaAMacro(CategoriaSimbolo.INFOCA);             // → TipologiaPTEL.BOMBEROS
 * categoriaAMacro(CategoriaSimbolo.ALOJAMIENTO);        // → TipologiaPTEL.GENERICO
 * ```
 */
export function categoriaAMacro(c: CategoriaSimbolo): TipologiaPTEL {
  switch (c) {
    // §3.1 Servicios (6)
    case CategoriaSimbolo.CENTRO_SANITARIO:        return TipologiaPTEL.SANITARIO;
    case CategoriaSimbolo.CENTRO_ENSENANZA:        return TipologiaPTEL.EDUCATIVO;
    case CategoriaSimbolo.ALOJAMIENTO:             return TipologiaPTEL.GENERICO;
    case CategoriaSimbolo.CONCURRENCIA_PUBLICA:    return TipologiaPTEL.GENERICO;
    case CategoriaSimbolo.SERVICIOS_SOCIALES:      return TipologiaPTEL.SOCIAL;
    case CategoriaSimbolo.BIBLIOTECA:              return TipologiaPTEL.CULTURAL;

    // §3.2 Emergencias (8)
    case CategoriaSimbolo.CECOPAL:                 return TipologiaPTEL.EMERGENCIAS;
    case CategoriaSimbolo.BOMBEROS:                return TipologiaPTEL.BOMBEROS;
    case CategoriaSimbolo.INFOCA:                  return TipologiaPTEL.BOMBEROS;
    case CategoriaSimbolo.POLICIA_LOCAL:           return TipologiaPTEL.POLICIAL;
    case CategoriaSimbolo.POLICIA_NACIONAL:        return TipologiaPTEL.POLICIAL;
    case CategoriaSimbolo.GUARDIA_CIVIL:           return TipologiaPTEL.POLICIAL;
    case CategoriaSimbolo.PROTECCION_CIVIL:        return TipologiaPTEL.EMERGENCIAS;
    case CategoriaSimbolo.APOYO_LOGISTICO:         return TipologiaPTEL.EMERGENCIAS;

    // §3.3 Transportes (3)
    case CategoriaSimbolo.HELIPUERTO:              return TipologiaPTEL.VIAL;
    case CategoriaSimbolo.GASOLINERA:              return TipologiaPTEL.COMBUSTIBLE;
    case CategoriaSimbolo.ANTENAS_COMUNICACIONES:  return TipologiaPTEL.TELECOM;

    // §3.4 Hidrografía (3)
    case CategoriaSimbolo.DEPOSITO_AGUA:           return TipologiaPTEL.HIDRAULICO;
    case CategoriaSimbolo.ETAP:                    return TipologiaPTEL.HIDRAULICO;
    case CategoriaSimbolo.EDAR:                    return TipologiaPTEL.HIDRAULICO;

    // §3.5 Energía (4)
    case CategoriaSimbolo.CENTRAL_ELECTRICA:       return TipologiaPTEL.ENERGIA;
    case CategoriaSimbolo.SUBESTACION_ELECTRICA:   return TipologiaPTEL.ENERGIA;
    case CategoriaSimbolo.ENERGIA_EOLICA:          return TipologiaPTEL.ENERGIA;
    case CategoriaSimbolo.ENERGIA_SOLAR:           return TipologiaPTEL.ENERGIA;

    // §3.6 Industria (2)
    case CategoriaSimbolo.INDUSTRIA:               return TipologiaPTEL.INDUSTRIAL;
    case CategoriaSimbolo.SUSTANCIAS_PELIGROSAS:   return TipologiaPTEL.INDUSTRIAL;

    // §3.7 Patrimonio (4)
    case CategoriaSimbolo.ELEMENTO_HISTORICO:      return TipologiaPTEL.CULTURAL;
    case CategoriaSimbolo.EDIFICIO_RELIGIOSO:      return TipologiaPTEL.RELIGIOSO;
    case CategoriaSimbolo.CAVIDAD:                 return TipologiaPTEL.GENERICO;
    case CategoriaSimbolo.ESPACIO_NATURAL:         return TipologiaPTEL.GENERICO;

    // §3.8 Equipamientos (6)
    case CategoriaSimbolo.EDIFICIO_PUBLICO:        return TipologiaPTEL.MUNICIPAL;
    case CategoriaSimbolo.CENTRO_CULTURAL:         return TipologiaPTEL.CULTURAL;
    case CategoriaSimbolo.ESPACIO_CULTURAL:        return TipologiaPTEL.CULTURAL;
    case CategoriaSimbolo.AREA_RECREATIVA:         return TipologiaPTEL.DEPORTIVO;
    case CategoriaSimbolo.PARQUE_OCIO:             return TipologiaPTEL.DEPORTIVO;
    case CategoriaSimbolo.DEPOSITO_INDUSTRIAL:     return TipologiaPTEL.INDUSTRIAL;

    default: {
      const _exhaustive: never = c;
      return _exhaustive;
    }
  }
}
